import { useCallback, useEffect, useMemo, useState } from 'react';
import { RootState, useAppSelector } from '../store';
import { Group, User, UserSettings } from '../Types';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { userService } from '../Services/userService';
import { groupService } from '../Services/groupService';

const useUser = () => {
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    let { userIdentifier } = useParams();
    const [user, setUser] = useState<User>();
    const [maxCosts, setMaxCosts] = useState<number>(0.0);
    const [userSnapshot, setUserSnapshot] = useState<User | null>(null);
    const [userLoaded, setUserLoaded] = useState<boolean>(false);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'user.toast.success', sourceName: 'Users' }));
    }, [i18n]);

    const handleSave = useCallback(() => {
        if (demo) {
            notifySuccess();
            return;
        }
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;

        const data = new URLSearchParams();
        data.append('maxCosts', maxCosts.toString());

        userService.saveUser(userIdentifier, data).then((response) => {
            setUserSnapshot(response.data);
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [demo, maxCosts, userIdentifier, userSettings.isLoggedIn, notifySuccess]);

    const handleMaxCostChange = useCallback((value: number) => {
        setMaxCosts(value);
    }, []);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;

        userService.getUser(userIdentifier).then((response) => {
            setUser(response.data);
            setUserSnapshot(response.data);
            setMaxCosts(parseFloat(parseFloat(response.data.maxCosts).toFixed(2)));
            setUserLoaded(true);
        });
    }, [userSettings.isLoggedIn, userIdentifier, demo]);

    const getGroupsByUser = useCallback((page: number, itemsPerPage: number, optionalParams?: Record<string, string>, query?: string|null) => {
        if (!userSettings.isLoggedIn) return;
        if (userIdentifier === undefined) return;
        return groupService.getGroupsByUser(page, itemsPerPage, userIdentifier, query);
    },[userSettings.isLoggedIn, userIdentifier]);

    const UserActions = useMemo(
        () => ({
            handleSave,
            handleMaxCostChange,
        }),
        [handleSave, handleMaxCostChange],
    );

    return {
        UserActions,
        userSnapshot,
        userLoaded,
        i18n,
        maxCosts,
        user,
        getGroupsByUser,
    };
};

export default useUser;
